import React, { useEffect, useRef, useState } from 'react';
import { apiUrl } from '../config/api';
import { getStoredUser } from '../utils/auth';
import { CameraCaptureModal } from './FormSiteTimestampControls';

// Certificate fields: immediate upload in the popup, saved with Create Company Employee on the form.
const CERT_ACCEPT =
  'image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf,.jpg,.jpeg,.png,.gif,.webp,.pdf';

function adminHeaders() {
  const user = getStoredUser();
  return { 'X-User-Email': user?.email || '' };
}

export function fileUrl(pathOrUrl) {
  if (!pathOrUrl) return '';
  if (String(pathOrUrl).startsWith('blob:') || String(pathOrUrl).startsWith('data:')) {
    return pathOrUrl;
  }
  return apiUrl(pathOrUrl);
}

export function formatCertDate(value) {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return text;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

/** True when expiry date is before today (local calendar day). */
export function isCertificateExpired(cert) {
  const text = String(cert?.expires_on || '').trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return false;
  const expiry = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return expiry < today;
}

export function formatCertDateTime(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const datePart = formatCertDate(text);
  const timeMatch = text.match(/T(\d{2}):(\d{2})/);
  if (!timeMatch) return datePart;
  return `${datePart} ${timeMatch[1]}:${timeMatch[2]}`;
}

/** Short display name: keep start + extension, e.g. long-name….png */
function shortenFileName(name, maxLen = 28) {
  const text = String(name || '').trim();
  if (!text || text.length <= maxLen) return text;
  const lastDot = text.lastIndexOf('.');
  const ext = lastDot > 0 && lastDot < text.length - 1 ? text.slice(lastDot) : '';
  const base = ext ? text.slice(0, lastDot) : text;
  const budget = Math.max(6, maxLen - ext.length - 1);
  return `${base.slice(0, budget)}…${ext}`;
}

export async function uploadClientCertificate({
  userId,
  file,
  files,
  name,
  issuedOn,
  expiresOn,
  description = '',
  notify = true,
}) {
  const body = new FormData();
  const uploadFiles = Array.isArray(files) && files.length
    ? files
    : file
      ? [file]
      : [];
  uploadFiles.forEach((item) => {
    if (item) body.append('certificates', item);
  });
  if (name) body.append('name', name);
  if (issuedOn) body.append('issued_on', issuedOn);
  if (expiresOn) body.append('expires_on', expiresOn);
  if (description) body.append('description', description);
  body.append('notify', notify ? 'true' : 'false');
  const res = await fetch(apiUrl(`/api/admin/clients/${userId}/certificates`), {
    method: 'POST',
    headers: adminHeaders(),
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data.detail;
    throw new Error(
      typeof detail === 'string' ? detail : 'Failed to save certificate'
    );
  }
  return data;
}

function getCertificateDraft({ name, issuedOn, expiresOn, description, file, files, notify }) {
  const certName = String(name || '').trim();
  const certDescription = String(description || '').trim();
  const uploadFiles = Array.isArray(files) && files.length
    ? files.filter(Boolean)
    : file
      ? [file]
      : [];
  const hasFile = uploadFiles.length > 0;
  if (!certName && !hasFile && !issuedOn && !expiresOn && !certDescription) {
    return { draft: null };
  }
  if (!certName && !hasFile) {
    return { error: 'Enter a certificate name or upload a file.' };
  }
  if (issuedOn && expiresOn && expiresOn < issuedOn) {
    return { error: 'Expiry date cannot be before issue date.' };
  }
  return {
    draft: {
      name: certName || uploadFiles[0]?.name || 'Certificate',
      issued_on: issuedOn || '',
      expires_on: expiresOn || '',
      description: certDescription,
      file: uploadFiles[0] || null,
      files: uploadFiles,
      notify: notify !== false,
    },
  };
}

function CertificateNotifyToggle({ on, onToggle, disabled = false, pending = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-busy={pending}
      disabled={disabled}
      onClick={() => onToggle(!on)}
      title={on ? 'Turn notifications off' : 'Turn notifications on'}
      aria-label={on ? 'Notifications on' : 'Notifications off'}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
        disabled ? 'pointer-events-none' : ''
      } ${on ? 'bg-emerald-500' : 'bg-gray-600'}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
          on ? 'translate-x-[1.125rem]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function CertificateInfoIcon({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 9v5" strokeLinecap="round" />
      <circle cx="10" cy="6.25" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function CertificateInfoPopup({ cert, onClose }) {
  const rows = [
    { label: 'Certificate name', value: String(cert.name || '').trim() || '—' },
    { label: 'Issue date', value: formatCertDate(cert.issued_on) || '—' },
    { label: 'Expiry date', value: formatCertDate(cert.expires_on) || '—' },
    { label: 'Certificate added date', value: formatCertDateTime(cert.created_at) || '—' },
    { label: 'Description', value: String(cert.description || '').trim() || '—' },
  ];

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-3"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-sm rounded-lg border border-gray-700 bg-gray-950 p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${cert.name} details`}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <h4 className="text-sm font-semibold text-white">Certificate info</h4>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xl leading-none text-gray-400 hover:bg-white/10 hover:text-white"
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        </div>
        <dl className="space-y-3">
          {rows.map((row) => (
            <div key={row.label}>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                {row.label}
              </dt>
              <dd className="mt-1 whitespace-pre-wrap break-words text-[12px] text-gray-200">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function CertificateInfoButton({ cert }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sky-300 hover:bg-sky-400/10 hover:text-sky-200"
        aria-label={`Certificate info for ${cert.name}`}
        title="Certificate info"
      >
        <CertificateInfoIcon />
      </button>
      {open ? <CertificateInfoPopup cert={cert} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

export function CertificatesNotifyRow({ on, onToggle, disabled = false }) {
  return (
    <div className="flex items-center justify-end gap-2">
      <span className="text-[11px] text-gray-400">Notifications for all certificates</span>
      <CertificateNotifyToggle on={on} onToggle={onToggle} disabled={disabled} />
    </div>
  );
}

export function EmployeeCertificateFields({
  onAdd,
  saving = false,
  compact = false,
  deferSave = false,
  showAddButton = false,
  fieldsRef = null,
  notifyOn = true,
}) {
  const galleryInputRef = useRef(null);
  const uploadMenuRef = useRef(null);
  const [name, setName] = useState('');
  const [issuedOn, setIssuedOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!showUploadMenu) return undefined;
    const handlePointerDown = (event) => {
      if (!uploadMenuRef.current?.contains(event.target)) {
        setShowUploadMenu(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [showUploadMenu]);

  useEffect(() => {
    if (!fieldsRef) return undefined;
    fieldsRef.current = {
      peekDraft: () =>
        getCertificateDraft({
          name,
          issuedOn,
          expiresOn,
          description,
          files,
          notify: notifyOn,
        }),
    };
    return () => {
      fieldsRef.current = null;
    };
  }, [fieldsRef, name, issuedOn, expiresOn, description, files, notifyOn]);

  const fieldClass = compact
    ? 'w-full rounded border border-gray-700 bg-black px-2 py-1.5 text-xs text-white [color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-gray-600'
    : 'w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-gray-600';
  const labelCls = compact
    ? 'mb-1 block text-[11px] font-medium text-gray-400'
    : 'mb-2 block text-sm font-medium text-gray-300';

  const reset = () => {
    setName('');
    setIssuedOn('');
    setExpiresOn('');
    setDescription('');
    setFiles([]);
    setLocalError('');
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  const handleAdd = async (nextFiles = files) => {
    const uploadFiles = Array.isArray(nextFiles)
      ? nextFiles.filter(Boolean)
      : nextFiles
        ? [nextFiles]
        : [];
    const result = getCertificateDraft({
      name,
      issuedOn,
      expiresOn,
      description,
      files: uploadFiles,
      notify: notifyOn,
    });
    if (result.error) {
      setLocalError(result.error);
      return;
    }
    if (!result.draft) {
      setLocalError('Enter a certificate name or upload a file.');
      return;
    }
    setLocalError('');
    try {
      await onAdd?.(result.draft);
      reset();
    } catch (err) {
      setLocalError(err?.message || 'Failed to add certificate');
    }
  };

  return (
    <>
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Certificate name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldClass}
            placeholder="e.g. First Aid"
            disabled={saving}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Issue date</label>
            <input
              type="date"
              value={issuedOn}
              onChange={(e) => setIssuedOn(e.target.value)}
              className={fieldClass}
              disabled={saving}
            />
          </div>
          <div>
            <label className={labelCls}>Expiry date</label>
            <input
              type="date"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              className={fieldClass}
              disabled={saving}
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>
            Description <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`${fieldClass} min-h-[4.5rem] resize-y`}
            placeholder="Optional notes about this certificate"
            disabled={saving}
            maxLength={2000}
            rows={compact ? 3 : 4}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="h-px flex-1 bg-gray-800" />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">or</span>
          <span className="h-px flex-1 bg-gray-800" />
        </div>
        <div>
          <label className={labelCls}>Upload file (optional)</label>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative" ref={uploadMenuRef}>
              <button
                type="button"
                onClick={() => setShowUploadMenu((open) => !open)}
                disabled={saving}
                className="inline-flex h-7 min-w-[5.5rem] items-center justify-center rounded border border-sky-300/50 bg-sky-400/20 px-3 text-[11px] font-semibold text-sky-200 hover:bg-sky-400/30 disabled:opacity-60"
              >
                Upload
              </button>
              {showUploadMenu ? (
                <div
                  className="absolute left-0 top-full z-[90] mt-1 w-48 max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-gray-700 bg-gray-900 py-1 shadow-lg"
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setShowUploadMenu(false);
                      galleryInputRef.current?.click();
                    }}
                    className="flex min-h-11 w-full items-center px-3 py-2 text-left text-xs font-semibold text-gray-200 hover:bg-gray-800 sm:min-h-0"
                  >
                    Choose from gallery
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setShowUploadMenu(false);
                      setShowCamera(true);
                    }}
                    className="flex min-h-11 w-full items-center px-3 py-2 text-left text-xs font-semibold text-gray-200 hover:bg-gray-800 sm:min-h-0"
                  >
                    Take picture
                  </button>
                </div>
              ) : null}
            </div>
            {showAddButton ? (
              <button
                type="button"
                onClick={() => handleAdd()}
                disabled={saving}
                className="inline-flex h-7 min-w-[5.5rem] items-center justify-center rounded bg-[#eda448] px-3 text-[11px] font-semibold text-gray-900 hover:bg-[#d8932e] disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Add certificate'}
              </button>
            ) : null}
            <span className="min-w-0 truncate text-[11px] text-gray-400">
              {files.length
                ? `${files.length} file${files.length === 1 ? '' : 's'} selected`
                : 'No file selected'}
            </span>
          </div>
          {files.length ? (
            <ul className="mt-2 max-h-24 min-w-0 space-y-1 overflow-y-auto overflow-x-hidden">
              {files.map((item, index) => (
                <li
                  key={`${item.name}-${item.size}-${index}`}
                  className="flex min-w-0 items-center justify-between gap-2 rounded border border-gray-800 bg-black/40 px-2 py-1"
                >
                  <span
                    className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-gray-300"
                    title={item.name}
                  >
                    {shortenFileName(item.name)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                    className="shrink-0 text-xs font-semibold text-red-400 hover:text-red-300"
                    aria-label={`Remove ${item.name}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <input
            ref={galleryInputRef}
            type="file"
            accept={CERT_ACCEPT}
            multiple
            className="hidden"
            onChange={(event) => {
              const picked = Array.from(event.target.files || []);
              event.target.value = '';
              if (!picked.length) return;
              const next = [...files, ...picked];
              setFiles(next);
              if (!deferSave && !showAddButton) {
                handleAdd(next);
              }
            }}
          />
        </div>
        {localError ? <p className="text-xs text-red-400">{localError}</p> : null}
      </div>
      {showCamera ? (
        <CameraCaptureModal
          multiple
          onCapture={(fileOrFiles) => {
            const picked = Array.isArray(fileOrFiles)
              ? fileOrFiles
              : fileOrFiles
                ? [fileOrFiles]
                : [];
            if (!picked.length) {
              setShowCamera(false);
              return;
            }
            const next = [...files, ...picked];
            setFiles(next);
            setShowCamera(false);
            if (!deferSave && !showAddButton) {
              handleAdd(next);
            }
          }}
          onClose={() => setShowCamera(false)}
        />
      ) : null}
    </>
  );
}

export function EmployeeCertificateList({
  certificates,
  onRemove,
  onNotify,
  saving = false,
  notifyPendingId = null,
}) {
  const [filter, setFilter] = useState('all'); // all | expired | active

  if (!certificates?.length) {
    return <p className="py-4 text-center text-sm text-gray-500">No certificates for now</p>;
  }

  const expiredCount = certificates.filter(isCertificateExpired).length;
  const filtered = certificates.filter((cert) => {
    const expired = isCertificateExpired(cert);
    if (filter === 'expired') return expired;
    if (filter === 'active') return !expired;
    return true;
  });

  const filterOptions = [
    { id: 'all', label: 'All', count: certificates.length },
    { id: 'expired', label: 'Expired', count: expiredCount },
    { id: 'active', label: 'Active', count: certificates.length - expiredCount },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-medium text-gray-400">Certificate list</p>
        <div
          className="inline-flex rounded-md border border-gray-700 bg-black p-px"
          role="group"
          aria-label="Filter certificates"
        >
          {filterOptions.map((option) => {
            const active = filter === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setFilter(option.id)}
                className={`rounded px-2 py-0.5 text-[10px] font-semibold leading-tight transition-colors ${
                  active
                    ? option.id === 'expired'
                      ? 'bg-red-500/90 text-white'
                      : 'bg-[#eda448] text-gray-900'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {option.label}
                <span className={`ml-1 ${active ? 'opacity-80' : 'text-gray-600'}`}>
                  ({option.count})
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {!filtered.length ? (
        <p className="py-4 text-center text-sm text-gray-500">
          {filter === 'expired'
            ? 'No expired certificates.'
            : filter === 'active'
              ? 'No active certificates.'
              : 'No certificates for now'}
        </p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((cert, index) => {
            const href = fileUrl(cert.url || cert.path);
            const expired = isCertificateExpired(cert);
            return (
              <li
                key={cert.id || `${cert.name}-${index}`}
                className={`grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 rounded px-1 py-1.5 ${
                  expired ? 'bg-red-500/5' : ''
                }`}
              >
                <div className="flex min-w-0 items-start gap-1.5">
                  <CertificateInfoButton cert={cert} />
                  <span className="w-5 shrink-0 pt-1 text-[12px] font-semibold text-gray-400">
                    {index + 1}.
                  </span>
                  <div className="min-w-0 pt-0.5">
                    <p
                      className="truncate text-[12px] font-semibold text-sky-300"
                      title={cert.name}
                    >
                      {cert.name}
                    </p>
                    {cert.expires_on ? (
                      <p
                        className={`text-[11px] ${
                          expired ? 'font-medium text-red-400' : 'text-gray-500'
                        }`}
                      >
                        {expired ? 'Expired' : 'Expires'} {formatCertDate(cert.expires_on)}
                      </p>
                    ) : (
                      <p className="text-[11px] text-gray-600">No expiry date</p>
                    )}
                  </div>
                </div>
                <div className="flex min-w-[4.5rem] justify-center">
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] font-semibold text-sky-300 underline hover:text-sky-200"
                      title={`Open ${cert.name}`}
                    >
                      View file
                    </a>
                  ) : (
                    <span className="text-[11px] text-gray-600">—</span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <CertificateNotifyToggle
                    on={cert.notify !== false}
                    onToggle={(next) => onNotify?.(cert, next)}
                    disabled={saving || notifyPendingId === cert.id}
                    pending={notifyPendingId === cert.id}
                  />
                  <button
                    type="button"
                    onClick={() => onRemove?.(cert)}
                    disabled={saving}
                    className="inline-flex h-8 w-8 items-center justify-center text-sm text-red-400 hover:text-red-300 disabled:opacity-50 sm:h-5 sm:w-5"
                    aria-label={`Remove ${cert.name}`}
                  >
                    ×
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
